import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, ScrollText, PiggyBank, HeartHandshake, Users, Briefcase, Building2, KeyRound,
  PenTool, Fingerprint, ArrowRight, Store, MonitorSmartphone, Video, MessageCircle,
  ShieldCheck, FileCheck2, UserCheck, CreditCard, BellRing, Handshake, ChevronDown, Mail,
  CalendarClock, Compass,
} from "lucide-react";
import { getServices, getFaqs } from "../lib/api";
import { SEO } from "../components/SEO";
import { useSettings } from "../lib/settings";
import { track } from "../lib/analytics";

const SHORTCUTS = [
  { icon: FileText, title: "730", desc: "Dichiarazione dei redditi", slug: "730" },
  { icon: ScrollText, title: "Successione", desc: "Pratiche ereditarie", slug: "successione" },
  { icon: PiggyBank, title: "Pensione", desc: "Conteggio e domande", slug: "conteggio-pensione" },
  { icon: HeartHandshake, title: "Invalidità civile", desc: "Domande e assistenza", slug: "invalidita-civile" },
  { icon: Users, title: "Assunzione badante", desc: "Colf, badanti, babysitter", slug: "assunzione-badante" },
  { icon: Briefcase, title: "Partita IVA", desc: "Apertura e gestione", slug: "apertura-partita-iva" },
  { icon: Building2, title: "Aprire un'impresa", desc: "Ditta o società", slug: "apertura-impresa" },
  { icon: KeyRound, title: "Contratto di locazione", desc: "Registrazione affitto", slug: "registrazione-contratto-locazione" },
  { icon: PenTool, title: "Firma digitale", desc: "Valore legale pieno", slug: "firma-digitale" },
  { icon: Fingerprint, title: "SPID", desc: "Identità digitale PA", slug: "spid" },
];

const USER_TYPES = [
  { title: "Privati e famiglie", desc: "Dichiarazioni, successioni, ISEE e pratiche fiscali.", to: "/privati-famiglie" },
  { title: "Pensionati", desc: "Pensioni, conteggi e prestazioni previdenziali.", to: "/pensioni-previdenza" },
  { title: "Lavoratori", desc: "730, CU, buste paga e diritti del lavoratore.", to: "/privati-famiglie" },
  { title: "Professionisti e Partite IVA", desc: "Apertura, forfettario, contabilità.", to: "/partite-iva-professionisti" },
  { title: "Imprese", desc: "Società, pratiche camerali, bilanci.", to: "/imprese-societa" },
  { title: "Proprietari di immobili", desc: "Contratti di affitto e adempimenti.", to: "/casa-locazioni" },
];

const STEPS = [
  "Scegli il servizio",
  "Controlla i documenti necessari",
  "Invia la richiesta o prenota un appuntamento",
  "Ti ricontattiamo sul canale che preferisci",
  "Seguiamo la tua pratica fino alla fine",
];

const TRUST = [
  { icon: UserCheck, title: "Assistenza dedicata", desc: "Un consulente di riferimento per la tua pratica." },
  { icon: FileCheck2, title: "Documenti chiari", desc: "Checklist precise prima di iniziare, senza sorprese." },
  { icon: Store, title: "Online e in sede", desc: "Scegli tu il canale: ufficio, video o web." },
  { icon: Handshake, title: "Consulenza personalizzata", desc: "Soluzioni sul tuo caso, non risposte standard." },
  { icon: CreditCard, title: "Costi chiari", desc: "Preventivi chiari prima di iniziare, senza sorprese." },
  { icon: BellRing, title: "Aggiornamenti sulla pratica", desc: "Ti avvisiamo noi a ogni passaggio." },
];

const BUSINESS = ["Apertura Partita IVA", "Regime forfettario", "Apertura società", "Contabilità", "Registro Imprese", "Pratiche CCIAA", "INPS", "Deposito bilancio", "Variazioni societarie", "Firma digitale"];
const FREE_TOOLS = [
  { icon: CalendarClock, title: "Scadenza registrazione contratto", desc: "Calcola la data principale da tenere sotto controllo per registrazione, proroga o risoluzione.", to: "/strumenti/scadenza-registrazione-contratto" },
  { icon: ScrollText, title: "Checklist successione guidata", desc: "Crea una prima lista dei documenti che potrebbero servire per la tua successione.", to: "/strumenti/checklist-successione" },
  { icon: KeyRound, title: "Quale contratto di locazione scegliere?", desc: "Orientati tra canone libero, concordato, transitorio, studenti e commerciale.", to: "/strumenti/quale-contratto-locazione" },
  { icon: Compass, title: "Quali servizi possono servirmi?", desc: "Rispondi a due domande e scopri i servizi FLASH più pertinenti per la tua esigenza.", to: "/strumenti/quali-servizi-mi-servono" },
];

const PENSION = ["Calcolo pensione", "Analisi contributiva", "Controllo estratto contributivo", "Consulenza pensionistica", "Domande pensionistiche", "Invalidità civile"];

const SectionTitle = ({ eyebrow, title, sub, dark }) => (
  <div className="max-w-2xl mb-12">
    {eyebrow && <div className={`text-xs font-bold uppercase tracking-[0.15em] mb-3 ${dark ? "text-[#FFC400]" : "text-[#F26522]"}`}>{eyebrow}</div>}
    <h2 className={`font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight ${dark ? "text-white" : "text-[#171717]"}`}>{title}</h2>
    {sub && <p className={`text-base mt-4 leading-relaxed ${dark ? "text-white/60" : "text-[#4B4B4B]"}`}>{sub}</p>}
  </div>
);

const HomePage = () => {
  const { waLink, settings } = useSettings();
  const [onlineServices, setOnlineServices] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    getServices({ online: true }).then((s) => setOnlineServices(s.slice(0, 6))).catch(() => {});
    getFaqs().then(setFaqs).catch(() => {});
  }, []);

  const wa = waLink();

  const HYBRID = [
    { icon: Store, tag: "IN SEDE", desc: "Ti aspettiamo nel nostro ufficio.", cta: "Prenota", to: "/prenota" },
    { icon: MonitorSmartphone, tag: "ONLINE", desc: "Richiedi i nostri servizi direttamente online.", cta: "Scopri i servizi online", to: "/servizi" },
    { icon: Video, tag: "TELEFONO / VIDEO", desc: "Parla con un consulente comodamente da casa.", cta: "Prenota una call", to: "/prenota?tipo=video" },
    wa
      ? { icon: MessageCircle, tag: "WHATSAPP", desc: "Scrivici e capiamo insieme cosa ti serve.", cta: "Apri WhatsApp", wa: true }
      : { icon: Mail, tag: "EMAIL", desc: "Scrivici e capiamo insieme cosa ti serve.", cta: "Scrivici", to: "/contatti" },
  ];

  return (
    <div>
      <SEO
        title="FLASH Service & Consulting — Centro servizi fiscali e digitali a Roma"
        description="Il tuo centro servizi in sede e online: 730, successioni, pensioni, partite IVA, imprese, SPID e firma digitale. Checklist documenti chiare e prenotazione facile."
      />

      <section data-testid="hero-section" className="relative bg-[#F5F5F5] pt-[72px] overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FFC400]/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-32 w-80 h-80 rounded-full bg-[#F26522]/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-[#E7E7E7] rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <img src="/flash-icon.png" alt="" className="h-4 w-4" />
              <span className="text-xs font-semibold text-[#4B4B4B]">Il tuo centro servizi, in sede e online</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#171717] leading-[1.05] tracking-tight">
              Tutto ciò che ti serve.
              <br />
              <span className="text-[#F26522]">In un unico posto.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#4B4B4B] leading-relaxed max-w-xl">
              CAF, Patronato, Pensioni, Successioni, Partite IVA, Imprese e servizi digitali.
              Online quando vuoi, in sede quando preferisci.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/servizi"
                data-testid="hero-find-service-button"
                className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D94E0F] text-white font-semibold rounded-full px-7 py-3.5 transition-colors shadow-[0_8px_24px_rgba(242,101,34,0.3)]"
              >
                Trova il servizio <ArrowRight size={17} />
              </Link>
              <Link
                to="/prenota"
                data-testid="hero-book-button"
                className="inline-flex items-center gap-2 bg-white border border-[#E7E7E7] hover:border-[#F26522] text-[#171717] font-semibold rounded-full px-7 py-3.5 transition-colors"
              >
                Prenota appuntamento
              </Link>
              {wa && (
                <a
                  href={wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("whatsapp_click", { placement: "hero" })}
                  data-testid="hero-whatsapp-link"
                  className="text-sm font-semibold text-[#25D366] hover:underline"
                >
                  Scrivici su WhatsApp
                </a>
              )}
            </div>
          </div>

          <div className="hidden lg:grid grid-cols-2 gap-4">
            {HYBRID.map((h, i) => (
              <div
                key={h.tag}
                className={`bg-white rounded-2xl border border-[#E7E7E7] p-6 shadow-[0_10px_30px_rgba(0,0,0,0.05)] ${i % 2 ? "translate-y-6" : ""}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#F26522]/10 flex items-center justify-center text-[#F26522] mb-4">
                  <h.icon size={20} />
                </div>
                <div className="font-display text-sm font-bold text-[#171717] mb-1">{h.tag}</div>
                <p className="text-xs text-[#4B4B4B] leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="shortcuts-section" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="Cosa devi fare?" title="Inizia dalla tua esigenza" sub="Niente nomi tecnici: scegli cosa devi fare, al resto pensiamo noi." />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {SHORTCUTS.map((s) => (
              <Link
                key={s.slug}
                to={`/servizi/${s.slug}`}
                data-testid={`shortcut-${s.slug}`}
                className="group bg-white rounded-2xl border border-[#E7E7E7] p-5 hover:border-[#F26522] hover:shadow-[0_10px_30px_rgba(242,101,34,0.1)] transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-[#F5F5F5] group-hover:bg-[#F26522]/10 flex items-center justify-center text-[#F26522] mb-3 transition-colors">
                  <s.icon size={19} />
                </div>
                <div className="text-sm font-bold text-[#171717]">{s.title}</div>
                <div className="text-xs text-[#4B4B4B] mt-0.5">{s.desc}</div>
                <ArrowRight size={15} className="text-[#F26522] mt-3 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/servizi" data-testid="shortcuts-all-services-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#F26522] hover:underline">
              Vedi tutti i servizi <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="usertypes-section" className="py-20 sm:py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle title="Trova subito i servizi per te" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USER_TYPES.map((u) => (
              <Link
                key={u.title}
                to={u.to}
                data-testid={`usertype-${u.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group bg-white rounded-2xl border border-[#E7E7E7] p-7 hover:border-[#F26522] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] transition-all"
              >
                <div className="font-display text-lg font-bold text-[#171717] group-hover:text-[#F26522] transition-colors">{u.title}</div>
                <p className="text-sm text-[#4B4B4B] mt-2 leading-relaxed">{u.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F26522] mt-4">
                  Scopri i servizi <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="hybrid-section" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle title="Scegli tu come essere seguito" sub="Siamo un centro servizi fisico con l'anima digitale: stesso team, stessa cura, su ogni canale." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HYBRID.map((h) => (
              <div key={h.tag} className="bg-[#F5F5F5] rounded-2xl p-7 flex flex-col border border-transparent hover:border-[#F26522]/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-[#F26522] mb-5">
                  <h.icon size={22} />
                </div>
                <div className="font-display text-base font-bold text-[#171717] mb-2">{h.tag}</div>
                <p className="text-sm text-[#4B4B4B] leading-relaxed flex-1">{h.desc}</p>
                {h.wa ? (
                  <a href={wa} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { placement: "hybrid" })} data-testid="hybrid-whatsapp-cta" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F26522] hover:underline">
                    {h.cta} <ArrowRight size={14} />
                  </a>
                ) : (
                  <Link to={h.to} data-testid={`hybrid-cta-${h.tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F26522] hover:underline">
                    {h.cta} <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="online-services-section" className="py-20 sm:py-24 bg-[#171717] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle dark eyebrow="Servizi online" title="Fai tutto online" sub="Questi servizi puoi richiederli direttamente dal sito: invii la richiesta e ti guidiamo a distanza." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {onlineServices.map((s) => (
              <div key={s.slug} data-testid={`online-service-${s.slug}`} className="bg-white/[0.06] rounded-2xl border border-white/10 p-6 flex flex-col hover:bg-white/[0.09] transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFC400] text-[#171717] rounded-full px-3 py-1">Online</span>
                  {s.estimatedTime && <span className="text-[11px] text-white/50">{s.estimatedTime}</span>}
                </div>
                <div className="font-display text-lg font-bold mb-2">{s.name}</div>
                <p className="text-sm text-white/60 leading-relaxed flex-1">{s.shortDescription}</p>
                <div className="flex items-center justify-between mt-5">
                  <span className="text-xs font-semibold text-white/70">{s.priceFrom ? `da €${s.priceFrom}` : "Contattaci per informazioni"}</span>
                  <Link
                    to={`/servizi/${s.slug}`}
                    data-testid={`online-service-cta-${s.slug}`}
                    className="inline-flex items-center gap-1.5 bg-[#F26522] hover:bg-[#D94E0F] text-white text-xs font-semibold rounded-full px-4 py-2 transition-colors"
                  >
                    Scopri <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="checklist-usp-section" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle
              eyebrow="Checklist documenti"
              title="Sai già cosa portare?"
              sub="Per ogni pratica trovi una checklist chiara dei documenti necessari. Così arrivi preparato, senza giri a vuoto."
            />
            <Link
              to="/servizi"
              data-testid="checklist-find-docs-button"
              className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D94E0F] text-white font-semibold rounded-full px-7 py-3.5 transition-colors"
            >
              Trova i documenti necessari <ArrowRight size={16} />
            </Link>
          </div>
          <div className="bg-[#F5F5F5] rounded-2xl border border-[#E7E7E7] p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-xs font-bold uppercase tracking-wider bg-[#F26522] text-white rounded-full px-3 py-1">Esempio: 730</span>
            </div>
            <ul className="space-y-3">
              {["Documento di identità", "Codice fiscale", "Certificazione Unica", "Spese sanitarie", "Documentazione mutuo"].map((d) => (
                <li key={d} className="flex items-center gap-3 text-sm text-[#171717]">
                  <span className="w-5 h-5 rounded-full bg-[#F26522]/10 flex items-center justify-center shrink-0">
                    <ShieldCheck size={12} className="text-[#F26522]" />
                  </span>
                  {d}
                </li>
              ))}
            </ul>
            <Link to="/servizi/730" data-testid="checklist-full-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F26522] mt-5 hover:underline">
              Vedi checklist completa <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      <section id="come-funziona" data-testid="how-it-works-section" className="py-20 sm:py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle title="Semplice dall'inizio alla fine." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {STEPS.map((step, i) => (
              <div key={step} data-testid={`how-step-${i + 1}`} className="bg-white rounded-2xl border border-[#E7E7E7] p-6 relative">
                <div className="font-display text-4xl font-extrabold text-[#FFC400] mb-3">{i + 1}</div>
                <p className="text-sm font-semibold text-[#171717] leading-snug">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="trust-section" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle title="Perché scegliere FLASH" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TRUST.map((t) => (
              <div key={t.title} className="flex gap-4 bg-white rounded-2xl border border-[#E7E7E7] p-6 hover:border-[#F26522]/40 transition-colors">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-[#F26522]/10 flex items-center justify-center text-[#F26522]">
                  <t.icon size={20} />
                </div>
                <div>
                  <div className="text-sm font-bold text-[#171717]">{t.title}</div>
                  <p className="text-xs text-[#4B4B4B] mt-1 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="business-area-section" className="py-20 sm:py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle eyebrow="Imprese" title="Servizi per imprese e professionisti" sub="Dall'apertura alla gestione ordinaria: un unico riferimento per la tua attività." />
            <Link to="/imprese-societa" data-testid="business-area-cta" className="inline-flex items-center gap-2 bg-[#171717] hover:bg-[#F26522] text-white font-semibold rounded-full px-7 py-3.5 transition-colors">
              Scopri i servizi per imprese <ArrowRight size={16} />
            </Link>
          </div>
          <div className="flex flex-wrap gap-2.5">
            {BUSINESS.map((b) => (
              <span key={b} className="bg-white border border-[#E7E7E7] rounded-full px-4 py-2 text-xs font-medium text-[#4B4B4B]">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="pension-area-section" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex flex-wrap gap-2.5 order-2 lg:order-1">
            {PENSION.map((p) => (
              <span key={p} className="bg-[#F5F5F5] border border-[#E7E7E7] rounded-full px-4 py-2 text-xs font-medium text-[#4B4B4B]">
                {p}
              </span>
            ))}
          </div>
          <div className="order-1 lg:order-2">
            <SectionTitle eyebrow="Pensioni" title="Quando andrai in pensione?" sub="Scoprilo con un'analisi chiara della tua posizione: date, importi e opzioni disponibili." />
            <Link to="/pensioni-previdenza" data-testid="pension-area-cta" className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D94E0F] text-white font-semibold rounded-full px-7 py-3.5 transition-colors">
              Scopri i servizi pensionistici <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="free-tools-section" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionTitle eyebrow="Gratis" title="Strumenti gratuiti" sub="Fai una prima verifica in pochi minuti e scopri cosa ti serve davvero." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FREE_TOOLS.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`free-tool-card-${t.to.split("/").pop()}`}
                className="group bg-[#F5F5F5] rounded-2xl border border-[#E7E7E7] p-6 hover:border-[#F26522] hover:bg-white hover:shadow-[0_14px_36px_rgba(242,101,34,0.1)] transition-all flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-white group-hover:bg-[#F26522]/10 flex items-center justify-center text-[#F26522] transition-colors">
                    <t.icon size={21} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFC400] text-[#171717] rounded-full px-3 py-1">Gratis</span>
                </div>
                <div className="font-display text-lg font-bold text-[#171717] group-hover:text-[#F26522] transition-colors">{t.title}</div>
                <p className="text-sm text-[#4B4B4B] mt-2 leading-relaxed flex-1">{t.desc}</p>
                <span className="mt-6 inline-flex items-center justify-center gap-2 bg-[#F26522] group-hover:bg-[#D94E0F] text-white text-xs font-semibold rounded-full px-5 py-2.5 transition-colors">
                  Prova lo strumento <ArrowRight size={13} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" data-testid="faq-section" className="py-20 sm:py-24 bg-[#F5F5F5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionTitle title="Domande frequenti" />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={f.q} data-testid={`faq-item-${i}`} className="bg-white rounded-2xl border border-[#E7E7E7] overflow-hidden">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="text-sm font-bold text-[#171717]">{f.q}</span>
                  <ChevronDown size={18} className={`text-[#F26522] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="px-6 pb-5 text-sm text-[#4B4B4B] leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="seo-text-section" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-2xl font-bold text-[#171717] mb-4">Il tuo centro servizi a Roma e online</h2>
          <p className="text-sm text-[#4B4B4B] leading-relaxed mb-3">
            FLASH Service & Consulting è un centro servizi con sede a Roma, in Via Francesco Cilea 7, e attivo online in tutta Italia.
            Ci occupiamo di dichiarazioni fiscali, successioni, locazioni, lavoro domestico, pensioni, partite IVA, pratiche d'impresa e servizi digitali
            come SPID e firma digitale. Ogni pratica parte da una checklist documenti chiara: sai sempre cosa serve, quanto costa e a che punto sei.
          </p>
          <p className="text-sm text-[#4B4B4B] leading-relaxed mb-6">
            Puoi venire in sede su appuntamento oppure gestire tutto online dalla tua area riservata: carichi i documenti, segui lo stato della pratica
            e ricevi aggiornamenti via email. Prima di iniziare, prova i nostri strumenti gratuiti per orientarti senza impegno.
          </p>
          <nav className="flex flex-wrap gap-2.5" aria-label="Aree di servizio" data-testid="seo-category-links">
            {[
              ["Privati e famiglie", "/privati-famiglie"], ["Casa e locazioni", "/casa-locazioni"],
              ["Lavoro domestico", "/lavoro-domestico"], ["Pensioni e previdenza", "/pensioni-previdenza"],
              ["Partite IVA e professionisti", "/partite-iva-professionisti"], ["Imprese e società", "/imprese-societa"],
              ["Servizi digitali", "/servizi-digitali"], ["Strumenti gratuiti", "/strumenti"],
            ].map(([label, to]) => (
              <Link key={to} to={to} className="text-xs font-semibold text-[#F26522] bg-[#F26522]/5 border border-[#F26522]/20 rounded-full px-4 py-2 hover:bg-[#F26522] hover:text-white transition-colors">
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <section data-testid="final-cta-section" className="py-20 sm:py-28 bg-[#171717] text-white relative overflow-hidden">
        <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-[#F26522]/20 blur-3xl pointer-events-none" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative">
          <img src="/flash-icon.png" alt="" className="h-14 w-14 mx-auto mb-6" />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">Non sai quale servizio ti serve?</h2>
          <p className="text-base text-white/60 mt-4 leading-relaxed">Scrivici o prenota un appuntamento. Ti aiutiamo a capire come procedere.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            {wa && (
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { placement: "final_cta" })}
                data-testid="final-cta-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:brightness-110 text-white font-semibold rounded-full px-7 py-3.5 transition-all"
              >
                <MessageCircle size={17} /> Scrivici su WhatsApp
              </a>
            )}
            <Link
              to="/prenota"
              data-testid="final-cta-book"
              className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#D94E0F] text-white font-semibold rounded-full px-7 py-3.5 transition-colors"
            >
              Prenota appuntamento
            </Link>
            {!wa && (
              <Link
                to="/contatti"
                data-testid="final-cta-contact"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-full px-7 py-3.5 transition-colors"
              >
                Contattaci
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
